import isEmpty from 'lodash/isEmpty';
import React, { Dispatch, Fragment, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { isArray } from 'lodash';

import { Loader } from 'shared/components/spinner/spinner';
import { useQueryParams } from 'shared/hooks/useQueryParams';
import { DownArrowIcon, LockIcon, RedCrossIcon, RedDeleteIcon, UpArrowIcon } from 'shared/icons/icon';
import { State } from 'shared/interface';
import i18n from 'shared/util/localization';
import localizationConstants from 'shared/util/translation/constants';
import { Translate } from 'shared/util/translation/translate';
import { customNumberFormatter, formatDate, replaceHashtagFromUrl } from 'shared/util/utility';
import PermissionManager from 'shared/hoc/permission/permissionManager';
import { adminPermissionMapping } from 'shared/constants/permissionConstant';
import { barSpanDropDown, chartUpdateFrequencyMapper, DATE_FORMAT_MAPPER } from 'shared/constants/constants';
import { ScaleType } from 'shared/interface/enums';

import { IChartDetails, IChartIndicators, IChartSettingsType, ISpan } from '../interface/futureTable';
import CustomIndicatorsDropdown from './CustomIndicatorsDropdown';
import ChartInfo from './chartInfo';

interface IProps {
	chartDetails: IChartDetails;
	color: string;
	indicators: IChartIndicators[];
	chartSettings: IChartSettingsType[];
	scaleType: string;
	headerIndex: number;
	spanObj: ISpan;
	currency: string;
	chartType: string;
	chartColor: string;
	setChartData: (chartData: any) => void;
	chartId: string;
	setChartSettings: Dispatch<SetStateAction<IChartSettingsType[]>>;
	setAllChartDetails: (chartDetails: { [key: string]: IChartDetails }) => void;
	setIsChartSettingUpdate: () => void;
	dataPoints: { id: string; label: string; checked: boolean }[];
	handleCheckboxChange: (id: string) => void;
	selectedFrequencies: { [materialKey: string]: string };
	setSelectedFrequencies: Dispatch<SetStateAction<{ [materialKey: string]: string }>>;
}
const ChartHeader: React.FC<IProps> = (props) => {
	const {
		chartDetails,
		color,
		indicators,
		headerIndex,
		chartSettings,
		scaleType,
		chartType,
		chartColor,
		setChartSettings,
		spanObj: { span, endDate, startDate },
		currency,
		setChartData,
		setAllChartDetails,
		chartId,
		setIsChartSettingUpdate,
		dataPoints,
		handleCheckboxChange,
		selectedFrequencies,
		setSelectedFrequencies
	} = props;
	const { language, admin, dateFormat } = useSelector((state: State) => state.siteConfig);
	const { t } = i18n;
	const { timeSpan: timeSpanParams, elementId } = useQueryParams();
	const navigate = useNavigate();
	const location = useLocation();
	const [isToggleTrue, setIsToggleTrue] = useState(false);

	// Get the current color from chartSettings
	const currentColor = useMemo(() => {
		const currentSetting = chartSettings.find((item) => item.id === chartId);
		return currentSetting?.color || color;
	}, [chartSettings, chartId, color]);

	// Function to determine which data type label to show based on the requirements
	const getDataTypeLabelToShow = useCallback(() => {
		// Get all available data types from data points
		const availableDataTypes = dataPoints
			.filter((point) => point.checked)
			.map((point) => {
				// Map data type key back to display name
				const keyToDataTypeMap: { [key: string]: string } = {
					s: 'settlement',
					l: 'letzter',
					h: 'hoch',
					e: 'erster',
					t: 'tief',
					o: 'oi',
					g: 'geld',
					b: 'brief',
					u: 'umsatz'
				};
				return keyToDataTypeMap[point.id] || point.id;
			});

		// If no data types are selected, don't show anything
		if (availableDataTypes.length === 0) {
			return '';
		}

		// Get the mainData from chartDetails to determine the available data types and their sequence
		const mainData = chartDetails?.fields || [];

		// Map mainData to data type names (assuming mainData contains the display names)
		const mainDataTypes = mainData.map((dataType: string) => {
			// Convert mainData display names to our internal data type names
			const mainDataToDataTypeMap: { [key: string]: string } = {
				Settlement: 'settlement',
				Letzter: 'letzter',
				Hoch: 'hoch',
				Erster: 'erster',
				Tief: 'tief',
				OI: 'oi',
				Geld: 'geld',
				Brief: 'brief',
				Umsatz: 'umsatz'
			};
			return mainDataToDataTypeMap[dataType] || dataType.toLowerCase();
		});

		// Use mainData sequence as the priority sequence, filtered by what's actually available
		const availableSequence = mainDataTypes.filter((type) => availableDataTypes.includes(type));

		if (availableSequence.length === 0) {
			return '';
		}

		// Find the default (first in mainData sequence that's available)
		const defaultDataType = chartDetails?.originalField;

		// Helper function to check if material has data for a specific data type
		const hasDataForDataType = (dataType: string) => {
			// Map data type to data type key
			const dataTypeToKeyMap: { [key: string]: string } = {
				settlement: 's',
				letzter: 'l',
				hoch: 'h',
				erster: 'e',
				tief: 't',
				oi: 'o',
				geld: 'g',
				brief: 'b',
				umsatz: 'u'
			};

			const dataTypeKey = dataTypeToKeyMap[dataType];
			if (!dataTypeKey) return false;

			// Check if this data type is available in the data points
			return dataPoints.some((point) => point.id === dataTypeKey && point.checked);
		};

		// Get all selected data types (based on checked data points)
		const selectedDataTypes = dataPoints
			.filter((point) => point.checked)
			.map((point) => {
				// Map data type key back to display name
				const keyToDataTypeMap: { [key: string]: string } = {
					s: 'settlement',
					l: 'letzter',
					h: 'hoch',
					e: 'erster',
					t: 'tief',
					o: 'oi',
					g: 'geld',
					b: 'brief',
					u: 'umsatz'
				};
				return keyToDataTypeMap[point.id] || point.id;
			});

		// Check if default is selected
		const isDefaultSelected = selectedDataTypes.includes(defaultDataType);

		// Case 1: Multiple selected including default - show default (only if it has data)
		if (selectedDataTypes.length > 1 && isDefaultSelected && hasDataForDataType(defaultDataType)) {
			return defaultDataType;
		}

		// Case 2: Only one selected - show that one (only if it has data). Hide only if it's the default AND default is 'settlement'
		if (selectedDataTypes.length === 1) {
			const onlySelectedType = selectedDataTypes[0];
			if (!hasDataForDataType(onlySelectedType)) {
				return '';
			}
			if (onlySelectedType === defaultDataType && defaultDataType === chartDetails?.originalField) {
				return '';
			}
			return onlySelectedType;
		}

		// Case 3: Multiple selected excluding default - show next in sequence after default (only if it has data)
		if (selectedDataTypes.length > 1 && !isDefaultSelected) {
			// Find the first selected value that comes after default in sequence
			for (let i = 0; i < availableSequence.length; i++) {
				const dataType = availableSequence[i];
				if (selectedDataTypes.includes(dataType) && hasDataForDataType(dataType)) {
					return dataType;
				}
			}
		}

		// Default case: don't show anything
		return '';
	}, [dataPoints, chartDetails]);

	const handleNavigationState = useCallback(
		(data: IChartSettingsType[]) => {
			if (data.length > 1) {
				const locationSpan = chartType === 'bar' ? (span === '1y' || span === '1m' || span === '1w' ? span : '1y') : span;
				const navigationPath = `/materials/chartScreen?timeSpan=${timeSpanParams || span}&currency=${currency}${
					elementId && `&element_id=${elementId}`
				}`;
				navigate(navigationPath, {
					state: {
						...(location?.state || {}),
						advanceChartConfig: {
							chartSettings: data,
							scaleType: scaleType
						},
						chartSelection: {
							type: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'line' : chartType,
							span: locationSpan,
							chartView:
								scaleType === ScaleType.PERCENTAGE_CHANGE
									? ScaleType.PERCENTAGE_CHANGE
									: scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
									? ScaleType.PERCENTAGE_CHANGE_TOTAL
									: scaleType,
							selectedFrequencies: selectedFrequencies
						}
					}
				});

				setChartSettings(data);
			} else if (chartSettings.length > 1 && data.length === 1) {
				const { boerse, symbol, basis_symbol } = data[0];
				setIsChartSettingUpdate();
				const navigationPath = `/materials/futureTable?boerse=${replaceHashtagFromUrl(boerse)}&basis_symbol=${
					basis_symbol ? replaceHashtagFromUrl(basis_symbol) : ''
				}&symbol=${replaceHashtagFromUrl(symbol)}&currency=${currency}&timeSpan=${timeSpanParams || span}&element_id=${elementId}`;

				navigate(navigationPath, {
					state: location.pathname.includes('chartScreen')
						? {
								advanceChartConfig: { chartSettings: data, scaleType: scaleType },
								chartSelection: {
									currency: data[0].currency.value,
									indicator: data[0]?.indicators || [],
									span: span,
									type:
										scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
											? 'line'
											: chartType,
									chartColor: data[0]?.color || chartColor,
									unit: data[0]?.unit.value || '',
									history: data[0]?.history.value || '',
									axis: data[0]?.axis.value || 'right',
									customChartSpan: {
										von: startDate,
										bis: endDate
									},
									chartView:
										scaleType === ScaleType.PERCENTAGE_CHANGE
											? ScaleType.PERCENTAGE_CHANGE
											: scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
											? ScaleType.PERCENTAGE_CHANGE_TOTAL
											: scaleType,
									selectedFrequencies: selectedFrequencies
								}
						  }
						: {}
				});
				setChartSettings(data);
			} else if (chartSettings.length === 1 && data.length === 1) {
				const { boerse, symbol, basis_symbol } = data[0];

				if (location.pathname.includes('materials/futureTable')) {
					const navigationPath = `/materials/futureTable?boerse=${replaceHashtagFromUrl(boerse)}&basis_symbol=${
						basis_symbol ? replaceHashtagFromUrl(basis_symbol) : ''
					}&symbol=${replaceHashtagFromUrl(symbol)}&currency=${currency}&timeSpan=${timeSpanParams || span}`;
					navigate(navigationPath, {
						state: {
							advanceChartConfig: {
								chartSettings: data,
								scaleType: scaleType
							},
							chartSelection: {
								currency: data[0].currency.value,
								indicator: data[0]?.indicators || [],
								span: span,
								type:
									scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'line' : chartType,
								chartColor: data[0]?.color || chartColor,
								unit: data[0]?.unit.value || '',
								history: data[0]?.history.value || '',
								axis: data[0]?.axis.value || 'right',
								customChartSpan: {
									von: startDate,
									bis: endDate
								},
								chartView:
									scaleType === ScaleType.PERCENTAGE_CHANGE
										? ScaleType.PERCENTAGE_CHANGE
										: scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
										? ScaleType.PERCENTAGE_CHANGE_TOTAL
										: scaleType,
								selectedFrequencies: selectedFrequencies
							}
						}
					});
				}
				setChartSettings(data);
			}
		},
		[
			chartSettings.length,
			chartType,
			span,
			timeSpanParams,
			elementId,
			navigate,
			location,
			scaleType,
			setIsChartSettingUpdate,
			currency,
			chartColor,
			startDate,
			endDate,
			setChartSettings,
			selectedFrequencies
		]
	);

	const deleteChartHeader = useCallback(() => {
		setChartData([]);
		setAllChartDetails({});
		const tempAdvanceChartConfig = [...chartSettings];
		const advanceChartConfigIndex = tempAdvanceChartConfig.findIndex((item) => `${item.id}` === `${chartSettings[headerIndex].id}`);
		tempAdvanceChartConfig.splice(advanceChartConfigIndex, 1);
		handleNavigationState(
			tempAdvanceChartConfig.map((item: any) => {
				return {
					...item,
					axis: {
						value:
							scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'left' : item.axis.value,
						label: scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL ? 'Left' : item.axis.label
					}
				};
			})
		); // Set navigation state
	}, [chartSettings, scaleType, handleNavigationState, headerIndex, setAllChartDetails, setChartData]);

	const deleteIndicators = useCallback(
		(index: number) => {
			const tempIndicatorArray = [...chartSettings[headerIndex].indicators];
			tempIndicatorArray.splice(index, 1);
			// update indicators element in  advance chart config
			const tempAdvanceChartConfig = [...chartSettings];
			const advanceChartConfigIndex = tempAdvanceChartConfig.findIndex(
				(item) =>
					`${item.symbol}-${item.boerse}-${item.id}` ===
					`${chartSettings[headerIndex].symbol}-${chartSettings[headerIndex].boerse}-${chartSettings[headerIndex].id}`
			);
			const chartObj = { ...tempAdvanceChartConfig[advanceChartConfigIndex], indicators: tempIndicatorArray };
			tempAdvanceChartConfig.splice(advanceChartConfigIndex, 1, chartObj);
			handleNavigationState(tempAdvanceChartConfig); // Set navigation state
		},
		[chartSettings, handleNavigationState, headerIndex]
	);

	const isAdminRight = useMemo(() => isArray(admin) && (admin.includes('superAdmin') || admin.includes('dataAdmin')), [admin]);

	const chartHistoryYear = chartSettings.find((item) => item.id === chartId && item.history.value);
	const dataTypeLabelToShow = getDataTypeLabelToShow();

	// Period dropdown precomputations
	const periodOptions = useMemo(() => {
		// Start with default barSpanDropDown options
		let options = barSpanDropDown(t);

		// Add dynamic options based on chartDetails.notierung
		if (chartDetails?.notierung === 'Q') {
			// Add quarterly option if not already present
			options = [...options, { label: `${t(localizationConstants.chartUpdateQuarterly)}`, value: 'quarterly' }];
		} else if (chartDetails?.notierung === '2') {
			// Add bi-monthly option if not already present
			options = [...options, { label: `${t(localizationConstants.biMonthly)}`, value: 'biMonthly' }];
		}

		return options;
	}, [t, chartDetails?.notierung]);

	const materialKey = useMemo(
		() => `${chartSettings[headerIndex].symbol}-${chartSettings[headerIndex].boerse}-${headerIndex}`,
		[chartSettings, headerIndex]
	);
	const rawPeriodValue = selectedFrequencies[materialKey] || (endDate && startDate ? 'custom' : span);
	const normalizeToSpanValue = useCallback((val: string) => {
		switch (val) {
			case 'D':
				return '1d';
			case 'W':
				return '1w';
			case 'M':
				return '1m';
			case 'Y':
				return '1y';
			default:
				return val;
		}
	}, []);
	const selectedSpanValue = useMemo(() => normalizeToSpanValue(rawPeriodValue as string), [normalizeToSpanValue, rawPeriodValue]);
	const periodTitle = useMemo(
		() => periodOptions.find((opt) => opt.value === selectedSpanValue)?.label || `${t(localizationConstants.period)}`,
		[periodOptions, selectedSpanValue, t]
	);

	return (
		<Fragment>
			<div
				className={`graph-header d-flex justify-content-space-between  ${
					isEmpty(indicators) && !location.search.includes('isIframe') ? 'mb-20' : 'mb-8'
				} ${chartDetails.disallowed === 0 ? '' : 'pr-25 bg-color--white b-radius--6px'}`}
			>
				{isEmpty(chartDetails) && <Loader className='full-width height--77px' />}
				{!isEmpty(chartDetails) && chartDetails.disallowed === 0 && (
					<>
						<div className='chart-title__wrapper d-flex'>
							<div
								className='color-div'
								style={{
									backgroundColor: currentColor
								}}
							/>
							<div className='title d-flex flex-column'>
								<div className='graph-title-action d-flex'>
									<div className='graph-title bg-color--white position--relative d-flex align-items-center justify-content-center'>
										<h2 className='d-flex align-items-center font--16px full-width line--height--22px font-weight--500 no-margin pl--25'>
											{(language === 'en' && chartDetails.name_eng !== '' && chartDetails.name_eng) || chartDetails.name}
											{chartSettings.length > 1 && chartHistoryYear?.history.value && ` - ${chartHistoryYear.history.value}`}
											{dataTypeLabelToShow && ` - ${dataTypeLabelToShow}`}

											<ChartInfo chartDetails={chartDetails} chartId={chartId} />
										</h2>
									</div>
									<div className='graph-action bg-color--white d-flex flex-column align-items-center justify-content-center '>
										{chartSettings.length > 1 && (
											<button
												disabled={chartSettings.length <= 1}
												onClick={deleteChartHeader}
												className='delete-icon--container d-flex cursor-pointer'
											>
												<RedDeleteIcon />
											</button>
										)}
										<button
											onClick={() => setIsToggleTrue(!isToggleTrue)}
											className='drop-down-icon--container d-flex align-items-center justify-content-center cursor-pointer'
										>
											{isToggleTrue ? <UpArrowIcon /> : <DownArrowIcon />}
										</button>
									</div>
								</div>
								{isToggleTrue && (
									<div className='data-points-checkboxes bg-color--white'>
										{dataPoints.map((point) => (
											<label
												key={point.id}
												style={{
													display: 'inline-flex',
													alignItems: 'center',
													marginRight: '20px',
													fontSize: '14px',
													color: '#2d3748',
													cursor: 'pointer',
													fontFamily: 'sans-serif'
												}}
											>
												<input
													type='checkbox'
													checked={point.checked}
													onChange={() => handleCheckboxChange(point.id)}
													style={{
														marginRight: '8px',
														width: '16px',
														height: '16px',
														accentColor: '#2d3748'
													}}
												/>
												{point.label}
											</label>
										))}
									</div>
								)}
							</div>
							<div className='frequency-section d-flex flex-column'>
								<div className={`graph-price d-flex align-items-center bg-color--white justify-content-space-between`}>
									<div className='d-flex mb-5px mt-5px justify-content-start flex-column align-items-start'>
										<h2 className='font--16px no-margin line--height--22px font-weight--500 d-flex justify-content-end align-items-center'>
											{customNumberFormatter(
												chartDetails?.[`price_${!dataTypeLabelToShow ? chartDetails?.originalField : dataTypeLabelToShow}`],
												chartDetails.nkstellen
											) || 0}
											&nbsp;
											{!['null', null].includes(chartDetails.waehrung) && (
												<>
													<span className='font--16px font-weight--500 line-height--22px'>
														{`${chartDetails.waehrung} /` || ''}
													</span>
													<span className='font--16px font-weight--500 line-height--22px '>{chartDetails.einheit}</span>
												</>
											)}
										</h2>
										<h5 className='font--16px no-margin line--height--22px font-weight--500 mb-5px'>
											{`${formatDate(chartDetails.kurszeit?.split('T')[0], DATE_FORMAT_MAPPER[dateFormat])}`}
										</h5>

										{chartDetails.notierung && (
											<h4 className='font-weight--400 font--16px no-margin line--height--22px mb-5px'>
												<Translate text={localizationConstants.update} />:{' '}
												{chartUpdateFrequencyMapper(t, chartDetails.notierung)}
											</h4>
										)}
										{isAdminRight && (
											<PermissionManager permission={adminPermissionMapping.superDataUserAdmin}>
												<h2 className='no-margin font--12px br--6px bg-light-orange padding-5 d-flex justify-content-center align-items-center '>
													Code: {chartDetails.code}
												</h2>
											</PermissionManager>
										)}
									</div>
								</div>
								{isToggleTrue && (
									<div className='indicator-section-1 bg-color--white'>
										<CustomIndicatorsDropdown
											title={periodTitle}
											options={chartType !== 'bar' ? periodOptions : []}
											handleClick={(value: string) => {
												setSelectedFrequencies((prev) => ({ ...prev, [materialKey]: value }));
											}}
											chartType={chartType === 'bar'}
											selectedData={selectedSpanValue}
											isDisable={isEmpty(chartDetails.fields)}
										/>
									</div>
								)}
							</div>
						</div>
					</>
				)}
				{!isEmpty(chartDetails) && chartDetails.disallowed === 1 && (
					<>
						<div className='chart-title__wrapper d-flex width--80'>
							<div
								className='color-div'
								style={{
									backgroundColor: currentColor
								}}
							/>
							<div className='graph-title align-items-center position--relative d-flex mt-12 mb-12'>
								<h2 className='d-flex align-items-center font--16px full-width line--height--22px font-weight--500 no-margin pl--25'>
									{(language === 'en' && chartDetails.name_eng !== '' && chartDetails.name_eng) || chartDetails.name}
									{chartSettings.length > 1 && chartHistoryYear?.history.value && ` - ${chartHistoryYear.history.value}`}
									<ChartInfo chartDetails={chartDetails} chartId={chartId} />
								</h2>
							</div>
						</div>
						<div className={`graph-price d-flex align-items-center justify-content-end`}>
							{chartDetails.disallowed === 1 && (
								<div className='d-flex justify-content-center align-items-center mr-20'>
									<LockIcon />
								</div>
							)}
							{chartSettings.length > 1 && (
								<button
									disabled={chartSettings.length <= 1}
									onClick={deleteChartHeader}
									className='delete-icon--container d-flex cursor-pointer'
								>
									<RedDeleteIcon />
								</button>
							)}
						</div>
					</>
				)}
			</div>
			{!isEmpty(indicators) && (
				<div className='indicators-container'>
					<div className='d-flex flex-wrap pl-72'>
						{indicators.map((item, index) => {
							return (
								<div className={`indicator-main`} key={`${item.symbol}-${item.boerse}-${index}`}>
									<div
										style={{
											backgroundColor: currentColor,
											opacity: 100 - (index + 1) * 10 + '%',
											width: '20px',
											height: '100%',
											borderRadius: '2px 0 0 2px'
										}}
									/>
									<h3>
										{item.value.toUpperCase()} (
										{item.value !== 'macd' && item.value !== 'sto'
											? item.calculatedValue
											: item.value === 'sto'
											? `${item.tage},${item.k}`
											: `${item.kurz},${item.lang}`}
										){' '}
									</h3>
									<span onClick={() => deleteIndicators(index)} className='cross-icon cursor-pointer d-flex '>
										<RedCrossIcon />
									</span>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</Fragment>
	);
};

export default ChartHeader;

// {chartDetails.faelligkeit && (
// 	<h2 className='maturity-text no-margin mb-8 font--16px padding-5 d-flex justify-content-center align-items-center'>
// 		<Translate text={localizationConstants.maturity} />: {chartDetails.faelligkeit}
// 	</h2>
// )}
{
	/* {chartDetails.notierung_zusatz && (
	<h4 className='font-weight--400 no-margin'>
	<Translate text={localizationConstants.additionalInfo} />: {chartDetails.notierung_zusatz}
	</h4>
)} */
}
